import { useEffect } from 'react';

/**
 * Gives every table cell the name of its column, so a phone can stack the rows.
 *
 * On a narrow screen a table has to become a list of rows, one field per line —
 * scrolling a register sideways is unreadable, and cropping it is worse. But a
 * stacked cell shows a bare "100" with no way to know it means students, because
 * the header it belonged to is three columns away and now hidden.
 *
 * The obvious fix is a data-label on each of the hundred-odd cells across twenty
 * files, which someone then has to remember for every table written afterwards.
 * This reads the label off the table's own header instead, so a new table is
 * handled the day it is written and nobody has to know this exists.
 *
 * Costs nothing on a desktop: the attribute is set either way and no stylesheet
 * reads it above the breakpoint.
 */
export function useStackedTables(): void {
  useEffect(() => {
    const label = () => {
      for (const table of document.querySelectorAll('table.table')) {
        const headers = [...table.querySelectorAll('thead th')].map((th) =>
          (th.textContent ?? '').trim(),
        );
        if (headers.length === 0) continue;

        for (const row of table.querySelectorAll('tbody tr')) {
          [...row.children].forEach((cell, index) => {
            const text = headers[index] ?? '';
            // Only when it changes. Writing the same value still counts as a
            // mutation, and the observer below would call this again for ever.
            if (cell.getAttribute('data-label') !== text) {
              cell.setAttribute('data-label', text);
            }
          });
        }
      }
    };

    label();

    // Rows arrive after a fetch and change on every filter, so this cannot be a
    // one-off at mount.
    const watcher = new MutationObserver(() => label());
    watcher.observe(document.body, { childList: true, subtree: true });
    return () => watcher.disconnect();
  }, []);
}
